<template>
  <div class="surveillance" ref="dialogContainer">
    <el-dialog :top="'1%'" v-bind:title="newCase._formMode === 'new' ? 'New Lab Result' : newCase._formMode === 'edit' ? 'Lab Result' : 'Additional Organisms'" v-model="newCaseDialogFormVisible" :close-on-click-modal="false" :close-on-press-escape="false" v-if="newCase">
      <div class="new-case-dialog" :class="{ isBatch: newCase._isBatch }">
        <el-form ref="observerForm" :model="newCase">
          <div v-if="newCase._isBatch === false" class="profile-photo">
            <img :src="clientPhoto(newCase.client)" />
          </div>

          <div v-if="newCase._isBatch === true">Please set a value on the items you want to change below</div>

          <template v-if="newCase._presetClient === false && newCase._formMode === 'new'">
            <el-form-item v-if="hasFacilityTypeFilter()" label="Location Type:" prop="newCaseFacType">
              <el-select v-model="newCase.newCaseFacType">
                <el-option value="LTC"></el-option>
                <el-option value="RET"></el-option>
              </el-select>
            </el-form-item>

            <el-form-item v-if="isAdminStaff()" label="Individual Type:" prop="clientType" :rules="[{ required: true, message: 'Client type is required' }]">
              <el-select v-model="newCase.clientType" filterable value-key="id" v-on:change="resetClientField">
                <el-option label="Resident" value="Client"></el-option>
                <el-option label="Staff" value="Staff"></el-option>
              </el-select>
            </el-form-item>

            <el-form-item v-if="sites && sites.length > 1" label="Facility:" prop="newCaseFac" :rules="[{ required: true, message: 'Facility is required' }]">
              <el-select v-model="newCase.newCaseFac" filterable placeholder="Not Selected" value-key="id">
                <el-option v-for="item in sitesForSelectedFacType" :key="item.id" :label="item.displayText" :value="item"> </el-option>
              </el-select>
            </el-form-item>

            <el-form-item v-if="newCase.clientType === 'Client'" label="Unit:" prop="newCaseUnit">
              <el-select v-model="newCase.newCaseUnit" clearable filterable placeholder="Not Selected" value-key="id">
                <el-option v-for="item in unitsForSelectedFac" :key="item.id" :label="item.displayText" :value="item.id"> </el-option>
              </el-select>
            </el-form-item>

            <el-form-item class="selectIndividual" ref="selectIndividual" label="Individual:" prop="client" :rules="[{ required: true, message: 'Individual is required' }]">
              <el-select v-model="newCase.client" :multiple="newCase._isBatch" filterable placeholder="Not Selected" value-key="id" :disabled="!newCase.newCaseFac && !canModify">
                <el-option v-for="item in clientsForSelectedFac" :key="item.id" :label="getClientName(item) + ' (' + getIdForItem(item) + ')'" :value="item">
                  <span v-if="item.status === 'D'" style="float: left">{{ getClientName(item) + " (" + getIdForItem(item) + ")" }} - (Discharged)</span>
                  <span v-else style="float: left">{{ getClientName(item) + " (" + getIdForItem(item) + ")" }}</span>
                </el-option>
              </el-select>
              <el-checkbox v-model="includeDischargedClient">Include Discharged Individuals</el-checkbox>
            </el-form-item>
          </template>

          <template v-if="newCase._presetClient || (newCase._formMode === 'edit' && isBatchEdit() === false) || newCase._formMode === 'addOrganism'">
            <div class="new-case-name">{{ newCase.client.firstName }} {{ newCase.client.middleName }} {{ newCase.client.lastName }} ({{ newCase.client.id }})</div>
          </template>

          <el-form-item label="Collection Date:" prop="collectionDate" :rules="[{ required: isBatchEdit() ? false : true, message: 'Collection date is required' }]">
            <el-date-picker v-model="newCase.collectionDate" type="date" editable clearable :format="$configStore.datePickerDateFormat()" :disabled="!canModify || newCase._formMode === 'addOrganism'"> </el-date-picker>
          </el-form-item>

          <el-form-item label="Culture Date:" prop="cultureDate" :rules="[{ required: isBatchEdit() ? false : true, message: 'Culture date is required' }]">
            <el-date-picker v-model="newCase.cultureDate" type="date" editable clearable :format="$configStore.datePickerDateFormat()" :disabled="!canModify || newCase._formMode === 'addOrganism'"> </el-date-picker>
          </el-form-item>

          <el-form-item label="Procedure:" prop="procedure" :rules="[{ required: isBatchEdit() === false && newCase.result !== 'Refused', message: 'Procedure is required' }]">
            <el-select v-model="newCase.procedure" :disabled="!canModify || newCase._formMode === 'addOrganism'">
              <el-option v-for="option in labProceduresOptions" :key="option.value" :value="option.value">{{ option.label }}</el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Result:" prop="result" :rules="[{ required: isBatchEdit() ? false : true, message: 'Result is required' }]">
            <el-select v-model="newCase.result" :disabled="!canModify">
              <el-option v-for="item in labResults" :key="item" :label="item" :value="item">
                {{ item }}
              </el-option>
            </el-select>
            <span v-if="newCase.result === 'Pending'">Please update Culture Date once lab result is known</span>
          </el-form-item>

          <el-form-item
            label="Organism:"
            prop="organism"
            :rules="[
              {
                required: isBatchEdit() === false && newCase.result !== 'Test Not Performed' && newCase.result !== 'Preliminary Negative - POC Device' && newCase.result !== 'Refused' && newCase.result !== 'Negative' && newCase.result !== 'Assumed Negative' && newCase.result !== 'Pending' && newCase.result !== 'Required',
                message: 'Organism is required'
              }
            ]"
          >
            <el-select v-model="newCase.organism" value-key="name" filterable placeholder="Not Selected" :disabled="!canModify">
              <el-option v-for="item in organisms" :key="item.name" :label="item.name" :value="item.name">
                {{ item.name }}
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item
            label="Source:"
            prop="source"
            :rules="[
              {
                required: isBatchEdit() === false && newCase.result !== 'Test Not Performed' && newCase.result !== 'Refused',
                message: 'Source is required'
              }
            ]"
          >
            <el-select v-model="newCase.source" :disabled="!canModify || newCase._formMode === 'addOrganism'">
              <el-option v-for="option in labSourceOptions" :key="option.value" :value="option.value">{{ option.label }}</el-option>
            </el-select>
          </el-form-item>

          <el-form-item
            label="Reason:"
            prop="reason"
            :rules="[
              {
                required: isBatchEdit() === false && newCase.result !== 'Test Not Performed' && newCase.result !== 'Refused',
                message: 'Reason is required'
              }
            ]"
          >
            <el-select v-model="newCase.reason" :disabled="!canModify || newCase._formMode === 'addOrganism'">
              <el-option v-for="option in labReasonOptions" :key="option.value" :value="option.value">{{ option.label }}</el-option>
            </el-select>
          </el-form-item>

          <el-form-item v-if="$configStore.labAttributableUnitEnabled() && newCase._formMode !== 'addOrganism' && !isBatchEdit()" label="Attributable Unit:" prop="unitId" :rules="[{ required: true, message: 'Attributable Unit is required' }]">
            <el-select v-model="newCase.unitId" clearable filterable placeholder="Not Selected" :disabled="!canModify">
              <el-option v-for="item in attributableUnitOptions" :key="item.id" :label="item.displayText" :value="item.id"> </el-option>
            </el-select>
          </el-form-item>

          <el-form-item
            v-if="!isBatchEdit()"
            class="labid"
            label="Lab ID:"
            prop="labId"
            :rules="[
              {
                required: newCase.result !== 'Test Not Performed' && newCase.result !== 'Refused',
                message: 'Lab ID is required'
              }
            ]"
          >
            <el-input v-model="newCase.labId" placeholder="Enter Lab ID" :disabled="!canModify || newCase._formMode === 'addOrganism' || newCase._isBatch || useAutoGeneratedLabId"></el-input>
            <el-checkbox v-model="useAutoGeneratedLabId" @change="createLabId($event, newCase)" label="Use auto-generated lab ID" :disabled="!canModify"></el-checkbox>
          </el-form-item>

          <el-form-item v-if="!isBatchEdit()" label="Notes:" prop="notes" :rules="[{ max: 1000, message: 'Notes must be 250 characters or less' }]">
            <el-input v-model="newCase.notes" :maxlength="1000" type="textarea" placeholder="Notes" :rows="2" :disabled="!canModify"></el-input>
            <character-count :limit="1000" :count="(newCase.notes || '').length" />
          </el-form-item>

          <div class="dialog-footer">
            <el-button data-testid="cancel-lab-result-wizard" @click="cancelLabResult()">Cancel</el-button>
            <el-button data-testid="add-lab-result" type="primary" v-if="canModify && newCase._formMode === 'new'" @click="addLabResult(newCase)" :disabled="!newCase.client">Add</el-button>
            <el-button data-testid="update-lab-result" type="primary" v-if="canModify && newCase._formMode === 'edit' && newCase._isBatch === false" @click="updateLabResult(newCase)">Update</el-button>
            <el-button data-testid="update-batch-lab-results" type="primary" v-if="canModify && newCase._formMode === 'edit' && newCase._isBatch === true" @click="emitBatchEditData(newCase)">Update</el-button>
            <el-button data-testid="add-lab-organism" type="primary" v-if="canModify && newCase._formMode === 'addOrganism'" @click="addLabResult(newCase)">Add</el-button>
          </div>
        </el-form>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import auth from "../../auth";
import util from "../../util";
import moment from "moment";
import { Loading } from "element-ui";
import CharacterCount from "../Shared/CharacterCount.vue";

export default {
  name: "lab-results-wizard",
  components: {
    "character-count": CharacterCount
  },
  props: {
    labResultData: Object,
    mode: String
  },
  data: function() {
    return {
      labResults: [],
      isRevera: false,
      newCaseFac: null,
      newCaseUnit: null,
      newCaseFacType: null,
      newClientSelection: null,
      canModify: false,
      isLoading: true,
      firstLoad: false,
      cases: [],
      precautions: [],
      symptomTypes: [],
      symptoms: [],
      clients: {},
      clientList: [],
      clientService: {},
      customSymptomTypeMap: {},
      sortedBy: "onSetDate",
      origins: [],
      outcomes: [
        {
          value: "Chronic",
          label: "Chronic"
        },
        {
          value: "Deceased",
          label: "Deceased"
        },
        {
          value: "Discharged",
          label: "Discharged"
        },
        {
          value: "Hospitalized",
          label: "Hospitalized"
        },
        {
          value: "Resolved",
          label: "Resolved"
        }
      ],
      statusImageMap: {
        GI: "bug2",
        UTI: "bug3",
        Respiratory: "bug1",
        Skin: "bug4"
      },
      organismOptionsSelection: [0],
      caseActiveTab: "1",
      selectedClientStatus: 0,
      selectedCase: null,
      selectedClient: null,
      selectedUnit: 0,
      selectedOrigin: 0,
      selectedStatus: 0,
      selectedTreatment: 0,
      selectedPrecautions: 0,
      selectedOrganisms: 0,
      selectedResolved: 0,
      selectedResult: 0,
      selectedSource: 0,
      selectedProcedure: 0,
      selectedReason: 0,
      selectedFamilyNotified: 0,
      selectedNextOfNotified: 0,
      selectedPhysicianNotified: 0,
      selectedClientType: 0,
      selectedSite: 0,
      selectedFirstName: "",
      selectedLastName: "",
      selectedCaseToDelete: null,
      dialogFormVisible: false,
      newCaseDialogFormVisible: false,
      newLabResultFormVisible: false,
      includeDischargedClient: false,
      useAutoGeneratedLabId: false,
      dialogConfirmDeleteVisible: false,
      newCase: {},
      subStatusMap: {
        GI: ["Noro Virus", "CDI", "GE"],
        UTI: ["ABUTI", "SUTI", "ASB"],
        Respiratory: ["Upper/Common Cold", "Lower/Pnem/Bron", "Flu - Influezna Like", "SINU"]
      },
      symptomDetailPage: 1,
      symptomDetailPageSize: 5,
      symptomDetailPageTotal: 0,
      symptomDetailTotal: 0,
      symptomDetailPageSizeOptions: [
        {
          label: "5 items per page",
          value: 5
        },
        {
          label: "10 items per page",
          value: 10
        },
        {
          label: "20 items per page",
          value: 20
        }
      ],
      filterDateRange: "",
      filteredSite: {},
      labReports: {},
      filterPeriods: "week",
      filterSymptomTypes: [],
      filteredSiteSubType: 0,
      organismOptions: [],
      availableUnits: [],
      organisms: [],
      units: [],
      sites: [],
      organization: [],
      summaryTableData: [],
      labTableData: [],
      labProceduresOptions: [],
      labSourceOptions: [],
      labReasonOptions: []
    };
  },
  computed: {
    shouldSkipClientLoad() {
      return this.newCase && this.newCase._presetClient && !this.newCase._isBatch;
    },
    symptomTypesWIthoutCommon: function() {
      return (this.symptomTypes || []).filter(s => s.type !== "Common");
    },
    sitesForSelectedFacType() {
      // if LTC or RET only, return all sites they have access to
      if (this.hasFacilityTypeFilter() === false) {
        return this.sites;
      }
      const type = this.newCase.newCaseFacType;
      this.newCase.newCaseFac = null;
      this.newCase.newCaseUnit = null;
      return this.sites.filter(s => s.unitType === type);
    },
    unitsForSelectedFac() {
      if (this.newCase.newCaseFac) {
        this.newCase.newCaseUnit = null;
        return this.newCase.newCaseFac.units.filter(u => u.code !== "PLACE_HOLDER" && u.archived !== true);
      }
      return [];
    },
    attributableUnitOptions() {
      const allUnits = (this.$configStore.data.units || []).filter((unit) => unit.archived !== true);
      const facId = (this.newCase && this.newCase.newCaseFac && this.newCase.newCaseFac.id) || (this.newCase && this.newCase.client && this.newCase.client.facId);
      if (facId) {
        const filtered = allUnits.filter(u => u.site && u.site.id === facId);
        const currentUnitId = this.newCase && this.newCase.unitId;
        if (currentUnitId && !filtered.some(u => u.id === currentUnitId)) {
          return allUnits;
        }
        return filtered;
      }
      return allUnits;
    },
    clientsForSelectedFac: function() {
      const vm = this;
      const unitId = this.newCase.newCaseUnit;
      const filterByUnit = this.newCase.clientType === "Client" && unitId !== null && unitId !== undefined && unitId !== "";
      const clients = (this.clientList || []).filter(c => {
        return (c.status !== "D" || vm.includeDischargedClient) && c.clientType === vm.newCase.clientType && (filterByUnit ? c.unitId === unitId : true);
      });
      const clientMap = new Map();
      clients.forEach(c => clientMap.set(c.id, c));
      return Array.from(clientMap.values());
    }
  },
  watch: {
    organismOptions: function(v) {
      console.log("organismOptions: ", v);
    },
    symptoms: function(v) {
      console.log(v);
      v.forEach(s => {
        console.log(s.classType, s.displayText);
      });
    },
    newCaseFac: function(v) {
      this.newCase.client = null;
      if (v === null) {
        this.clientList = [];
      } else {
        // start loading
        this.newCaseFacSelection();
      }
    },
    "newCase.newCaseFac": function(v) {
      if (this.newCase._presetClient === false && this.newCase._formMode !== "edit" && this.newCase._formMode !== "addOrganism") {
        if (this.newCase._isBatch) {
          this.newCase.client = [];
        } else {
          this.newCase.client = null;
        }
      }
      if (v === null) {
        this.clientList = [];
      } else {
        // start loading
        this.newCaseFacSelection();
      }
    },
    "newCase.procedure": function(v) {
      if (this.isRevera) {
        if (v === "POC Device") {
          this.labResults = ["Preliminary Positive - POC Device", "Preliminary Negative - POC Device", "Preliminary Indeterminate - POC Device", "Refused"];
          this.labSourceOptions = ["Self Swab", "Deep Nasal", "Combined Throat and Nares", "Shallow anterior nasal swab", "NP swab, Nasopharynx"].map(s => {
            return {
              label: s,
              value: s
            };
          });
        } else if (v === "Respiratory Culture") {
          this.labResults = ["Positive", "Indeterminate", "Negative", "Assumed Negative", "Pending", "Refused", "Test Not Performed"];
          this.labSourceOptions = ["Deep Nasal", "NP swab", "Nares", "Nasopharynx", "Aspiration", "Saliva", "Sputum", "Throat"].map(s => {
            return {
              label: s,
              value: s
            };
          });
        } else {
          this.labResults = this.$configStore.labResults();
          this.labSourceOptions = (this.$configStore.data.config.LabSource || []).map(s => {
            return {
              label: s,
              value: s
            };
          });
        }
      }
    },
    filterDateRange: function(v) {
      console.log("filterDateRange", v);
    },
    newCaseDialogFormVisible: function(value) {
      if (value === false) {
        this.$emit("wizard-closed");
      }
    }
  },
  methods: {
    hasFacilityTypeFilter() {
      return auth.userInfo().roles.indexOf("ROLE_LTC") >= 0 && auth.userInfo().roles.indexOf("ROLE_RET") >= 0;
    },
    createLabId(event, newCase) {
      if (event.target.checked) {
        newCase.labId = `${this.labIdPrefix}${moment().format()}`;
      } else {
        newCase.labId = "";
      }
    },
    isAdminStaff: function() {
      return auth.userInfo().roles.indexOf("ROLE_ADMIN_STAFF") >= 0;
    },
    newCaseFacSelection: async function() {
      console.log("newCaseFacSelection");
      if (!this.newCase.newCaseFac) {
        return;
      }
      if (this.shouldSkipClientLoad) {
        return;
      }

      let loadingInstance = Loading.service({ fullscreen: true });
      let vm = this;
      const clients = await util.chunkedClientFetch(this.$http, this.sites.length > 1 ? this.newCase.newCaseFac.id : null);
      vm.clientList = clients;
      this.$nextTick(() => {
        loadingInstance.close();
      });
    },
    nameParser: function(c) {
      let vm = this;
      if (c.status !== "D" || vm.includeDischargedClient) {
        let value = c.firstName + (c.middleName ? " " + c.middleName + " " : " ") + c.lastName;
        value = value + "(" + (this.$configStore.patientIdType() ? c[this.$configStore.patientIdType()] : c.patientId) + ")";
        if (c.status === "D") {
          value = value + "- (Discharged)";
        }

        return value;
      }

      return null;
    },
    querySearchAsync: function(queryString, cb) {
      let vm = this;
      if (this.newCase.client && this.nameParser(this.newCase.client) !== queryString) {
        this.newCase.client = null;
      }

      var url = window.CONFIG.legacy_facade_api + "/clients/list/unit/0/page/0?&sort=firstName&name=" + queryString;
      this.$http.get(url).then(response => {
        let clients = [];
        if (response.body) {
          clients = response.body.content; // body['_embedded'].clients
        }

        let results = [];
        clients.forEach(c => {
          let value = vm.nameParser(c);
          if (value) {
            results.push({
              value: value,
              item: c
            });
          }
        });

        cb(results);
      });
    },
    newClientSelected: function(selection) {
      console.log(this.newClientSelection);
      this.newCase.client = selection.item;
    },
    filterUpdated: function(o) {
      this.selectedUnit = o.unit;
      this.selectedSite = o.site;
      this.availableUnits = o.units || [];
      this.availableFacs = o.sites ? o.sites : 0;
    },
    toggleSection: function(section) {
      this.toggleSectionVisibility[section] = !this.toggleSectionVisibility[section];
    },
    filterPrecautions: function(precautions) {
      let vm = this;
      return (precautions || []).filter(p => vm.precautionById(p.precautionId));
    },
    validSymptoms: function(newCase) {
      return (this.symptoms || []).filter(s => {
        return newCase.symptomType && s.classType === newCase.symptomType.type;
      });
    },
    updatePersonalNotes(newNote) {
      console.log(newNote);
      if (this.clients[this.selectedClient.clientId]) {
        var clientData = this.clients[this.selectedClient.clientId];
        clientData.notes.splice(0, 0, newNote);
      }
    },
    handleChange(value) {
      console.log(value);
      this.filteredSite = value[0];
      this.filteredSiteSubType = value[1] || 0;

      this.symptomTypeUpdated(value[0]);
    },
    getsubStatusMap: function(caseData) {
      // console.log(caseData)
      if (caseData && caseData.symptomType && caseData.symptomType.subTypes) {
        // console.log(this.subStatusMap[caseData.symptomType.text])
        // console.log('getsubStatusMap subtypes')
        return caseData.symptomType.subTypes;
      }

      // console.log('getsubStatusMap')
      return [];
    },
    buildParam: function() {
      var rUrl = "";

      if (this.filterDateRange !== "" && this.filterDateRange[0] !== "") {
        console.log("this.filterDateRange", this.filterDateRange);
        var after = this.filterDateRange[0] || new Date();
        var before = this.filterDateRange[1] || new Date();

        after = moment(after)
          .hours(0)
          .minutes(0)
          .seconds(0)
          .toDate();
        before = moment(before)
          .hours(23)
          .minutes(59)
          .seconds(59)
          .toDate();

        rUrl = "start=" + encodeURIComponent(after) + "&end=" + encodeURIComponent(before);
      }
      rUrl = rUrl + "&types=" + this.filterSymptomTypes;

      if (this.filteredSiteSubType !== 0) {
        rUrl = rUrl + "&subType=" + this.filteredSiteSubType;
      }

      if (this.selectedOrigin !== 0) {
        rUrl = rUrl + "&origin=" + this.selectedOrigin;
      }

      if (this.selectedStatus !== 0) {
        rUrl = rUrl + "&confirmed=" + (this.selectedStatus === 1 ? "true" : "false");
      }

      if (this.selectedPrecautions !== 0) {
        rUrl = rUrl + "&precaution=" + this.selectedPrecautions;
      }

      if (this.selectedOrganisms !== 0) {
        rUrl = rUrl + "&organism=" + this.selectedOrganisms;
      }

      if (this.selectedResolved !== 0) {
        if (this.selectedResolved === "Resolved" || this.selectedResolved === "Not Resolved") {
          rUrl = rUrl + "&resolved=" + (this.selectedResolved === "Resolved" ? "true" : "false");
        } else {
          rUrl = rUrl + "&outcome=" + this.selectedResolved;
        }
      }

      if (this.selectedFamilyNotified !== 0) {
        rUrl = rUrl + "&familyNotified=" + (this.selectedFamilyNotified === 1 ? "true" : "false");
      }

      if (this.selectedNextOfNotified !== 0) {
        rUrl = rUrl + "&nextOfNotified=" + (this.selectedNextOfNotified === 1 ? "true" : "false");
      }

      if (this.selectedPhysicianNotified !== 0) {
        rUrl = rUrl + "&physicianNotified=" + (this.selectedPhysicianNotified === 1 ? "true" : "false");
      }

      if (this.selectedClientType !== 0) {
        rUrl = rUrl + "&clientType=" + (this.selectedClientType === 1 ? "Client" : "Staff");
      }

      if (this.selectedFirstName !== "") {
        rUrl = rUrl + "&firstName=" + this.selectedFirstName;
      }

      if (this.selectedLastName !== "") {
        rUrl = rUrl + "&lastName=" + this.selectedLastName;
      }

      if (this.selectedTreatment !== 0) {
        rUrl = rUrl + "&treatmentId=" + this.selectedTreatment;
      }

      if (this.selectedClientStatus !== 0) {
        rUrl = rUrl + "&clientActive=" + (this.selectedClientStatus === 1 ? "true" : "false");
      }

      if (Array.isArray(this.availableUnits) && this.availableUnits.length > 0) {
        rUrl = rUrl + "&units=" + this.availableUnits;
      }

      if (this.selectedSite > 0) {
        rUrl = rUrl + "&facIds=" + [this.selectedSite];
      } else if (this.availableFacs !== 0 && Array.isArray(this.availableFacs)) {
        rUrl = rUrl + "&facIds=" + this.availableFacs;
      }

      rUrl = rUrl + "&sort=" + this.sortedBy;

      console.log(rUrl);
      return rUrl;
    },
    getFiltersAsQueryString: function() {
      const vm = this;
      const filters = {
        start: vm.filterDateRange[0],
        end: vm.filterDateRange[1],
        site: vm.selectedSite,
        unit: vm.selectedUnit,
        organisms: vm.selectedOrganisms,
        result: vm.selectedResult,
        source: vm.selectedSource,
        procedure: vm.selectedProcedure,
        reason: vm.selectedReason,
        clientType: vm.selectedClientType,
        firstName: vm.selectedFirstName,
        flastName: vm.selectedLastName
      };
      filters.clientType = filters.clientType === 0 ? null : filters.clientType === 1 ? "Client" : "Staff";
      return Object.entries(filters)
        .filter(entry => entry[1])
        .map(entry => `${entry[0]}=${entry[1]}`)
        .join("&");
    },
    clientName: function(symptomDetail) {
      var clientData = symptomDetail.client;
      this.clients[symptomDetail.clientId] = clientData;
      return this.getClientName(clientData);
    },
    getClientName: function(clientData) {
      var firstName = clientData.firstName ? clientData.firstName + " " : "";
      var middleName = clientData.middleName ? clientData.middleName + " " : "";
      var lastName = clientData.lastName ? clientData.lastName : "";

      // console.log(JSON.stringify(clientData, ' ', 2))
      return firstName + middleName + lastName;
    },
    sortedSymptom(symptoms) {
      // <el-tag v-for="d in scope.row.symptoms
      if (!symptoms || symptoms.length <= 1) {
        return symptoms;
      }

      // console.log(JSON.stringify(symptoms, ' ', 2))
      // classType: "GI"
      // colorCode: "0"
      // displayText: "Diarrhea"
      let sortedSymptoms = [];
      symptoms.forEach(s => {
        sortedSymptoms.push(s);
      });
      sortedSymptoms.sort(function(a, b) {
        let compA = a.classType || "";
        let compB = b.classType || "";

        if (compA === "GI") compA = "a" + a.displayText;
        else if (compA === "Respiratory") compA = "b" + a.displayText;
        else if (compA === "UTI") compA = "c" + a.displayText;
        else compA = "d" + a.displayText;

        if (compB === "GI") compB = "a" + b.displayText;
        else if (compB === "Respiratory") compB = "b" + b.displayText;
        else if (compB === "UTI") compB = "c" + b.displayText;
        else compB = "d" + b.displayText;

        let x = compA.toLowerCase();
        let y = compB.toLowerCase();
        if (x < y) {
          return -1;
        }
        if (x > y) {
          return 1;
        }
        return 0;
      });

      return sortedSymptoms;
    },
    loadImage: function(detail, callback) {
      var vm = this;
      if (this.clients[detail.clientId] || this.clients[detail.clientId] === false) {
        callback();
        return;
      }
      this.clients[detail.clientId] = false;
      this.$http.get(window.CONFIG.legacy_facade_api + "/clients/" + detail.clientId).then(
        response => {
          // console.log(JSON.stringify(response.body, ' ', 2))
          vm.$set(vm.clients, detail.clientId, response.body);
          vm.$forceUpdate();
          callback();
        },
        response => {
          // error
          callback();
        }
      );
    },
    statusImage: function(detail) {
      var result = "";
      var filename;
      if (detail.symptomType) {
        filename = this.statusImageMap[detail.symptomType];
        if (!detail.confirmed) {
          filename = filename + "s";
        }
        result = "/static/" + filename + ".png";
      }

      if (result === "") {
        filename = "bug4";
        if (!detail.confirmed) {
          filename = filename + "s";
        }
        result = "/static/" + filename + ".png";
      }

      return result;
    },
    tagType: function(type) {
      if (type.colorCode === "0") {
        return "success";
      } else if (type.colorCode === "1") {
        return "primary";
      } else if (type.colorCode === "2") {
        return "warning";
      } else if (type.colorCode === "3") {
        return "danger";
      }
      return "gray";
    },
    organismById: function(organId) {
      var organName = "Not Selected";
      if (organId === 0) {
        return organName;
      }

      this.organisms.forEach(function(o) {
        if (o.id === organId) {
          organName = o.name;
        }
      });

      return organName;
    },
    treatmentById: function(treatmentId) {
      var name = "Not Selected";
      if (treatmentId === 0) {
        return name;
      }

      this.treatments.forEach(function(t) {
        if (t.id === treatmentId) {
          name = t.displayText;
        }
      });

      return name;
    },
    getTreatments: function(caseData) {
      let result = "";
      caseData.treatmentDetails.forEach(d => {
        // if (d.endDate) {
        //   return
        // }

        if (result.length > 0) {
          result = result + ", ";
        }
        if (d.customTreatment) {
          result = result + d.customTreatment;
        } else if (d.treatment && d.treatment.displayText) {
          result = result + d.treatment.displayText;
        }
      });

      if (result === "") {
        result = "Not Selected";
      }

      return result;
    },
    labResult: function(caseData) {
      let result = "Not Selected";
      let reports = this.labReports[caseData.id] || [];
      reports.forEach(l => {
        if (l.organism === caseData.primaryOrganism) {
          result = l.result || "Not Selected";
        }
      });

      return result;
    },
    unitById: function(unitId) {
      var unitName = "";
      this.units.forEach(function(u) {
        if (u.id === unitId) {
          unitName = u.displayText;
        }
      });

      return unitName;
    },
    facilityNameByUnitId: function(unitId) {
      var name = "";
      this.units.forEach(function(u) {
        if (u.id === unitId) {
          name = u.site.displayText;
        }
      });

      return name;
    },
    originById: function(originId) {
      var originName = "Not Selected";
      this.origins.forEach(function(o) {
        if (o.value === originId && o.value !== 0) {
          originName = o.label;
        }
      });
      return originName;
    },
    unitByName: function(unitName) {
      var unit = null;
      this.units.forEach(function(u) {
        if (u.displayText === unitName) {
          unit = u;
        }
      });

      return unit;
    },
    precautionById: function(precautionId) {
      var name = "";
      this.precautions.forEach(function(p) {
        if (p.id === precautionId) {
          name = p.displayText;
        }
      });

      return name;
    },
    clientPhoto: function(client) {
      if (client && client.photo) {
        var f = client.photo;
        if (f.indexOf("/") !== -1) {
          return window.CONFIG.api + "/" + f + "?access_token=" + auth.getToken();
        }

        return "/static/" + (client.photo ? client.photo : "user") + ".png";
      }

      return "/static/user.png";
    },
    canSave: function(symptomDetail) {
      var canSave = false;
      symptomDetail.symptoms.forEach(function(s) {
        if (symptomDetail.symptomType === s.classType) {
          canSave = true;
        } else if (symptomDetail.infectionSite === s.classType) {
          canSave = true;
        }

        // console.log('- canSave ' + symptomDetail.symptomType + '- ' + s.classType)
        // console.log(symptomDetail)
        // console.log(s)
        // console.log('symptomDetail.infectionSite: ' + symptomDetail.infectionSite)
      });

      // console.log('canSave ' + (canSave ? 'Yes' : 'NO'))

      return canSave;
    },
    reverse: function(array) {
      return array.slice().reverse();
    },
    isStaff: function(client) {
      if (client) {
        return client.clientType === "Staff";
      } else {
        return false;
      }
    },
    selectedRow: function(row, event, column) {
      const data = JSON.parse(JSON.stringify(row));
      data._formMode = "edit";
      this.showCaseDialog(data);
    },
    addOrganism: function(index, row, event) {
      event.stopPropagation();
      const data = JSON.parse(JSON.stringify(row));
      data._formMode = "addOrganism";
      this.showCaseDialog(data);
    },
    cancelLabResult: function() {
      this.newCaseDialogFormVisible = false;
      this.newCase = {};
    },
    showCaseDialog: function(selectedCase) {
      this.newClientSelection = null;
      this.newCase = {
        _formMode: "new",
        _presetClient: false,
        _isBatch: false,
        newCaseFacType: "LTC",
        clientType: "Client",
        newCaseFac: this.sites && this.sites.length > 1 ? null : this.sites[0],
        newCaseUnit: null,
        unitId: null,
        client: null,
        collectionDate: null,
        cultureDate: null,
        organism: null,
        result: null,
        reason: null,
        procedure: null,
        source: null,
        labId: null,
        notes: ""
      };
      if (selectedCase) {
        Object.assign(this.newCase, selectedCase);
      }
      if (this.newCase._formMode === "addOrganism") {
        this.newCase.organism = null;
        this.newCase.result = null;
      }
      if (this.newCase._isBatch && this.newCase._formMode === "new") {
        this.newCase.labId = `BATCH-${moment().format("YYYYMMDD-HHmmss")}`;
      }
      this.newCaseDialogFormVisible = true;
      if (this.$refs.dialogContainer) {
        document.body.append(this.$refs.dialogContainer);
      }

      this.$nextTick(() => {
        if (this.$refs.observerForm) {
          this.$refs.observerForm.resetFields();
        }
      });
    },
    isBatchEdit() {
      return this.newCase._formMode === "edit" && this.newCase._isBatch === true;
    },
    isBatchEntry() {
      return this.newCase._formMode === "new" && this.newCase._isBatch === true;
    },
    resetClientField: function() {
      this.$refs.selectIndividual.resetField();
    },
    addLabResult: async function(newCase) {
      this.$refs["observerForm"].validate(async valid => {
        if (valid) {
          var loadingInstance = Loading.service({ fullscreen: true });
          const originalCaseData = JSON.parse(JSON.stringify(newCase));
          let batchClients = [];
          delete newCase._formMode;
          delete newCase._isBatch;
          delete newCase._presetClient;
          delete newCase.newCaseFac;
          delete newCase.id;
          delete newCase.uuid;

          if (originalCaseData._formMode === "new") {
            newCase.clientType = newCase.clientType || "Client";
            if (originalCaseData._isBatch) {
              batchClients = newCase.client;
            } else {
              newCase.clientId = newCase.client.id;
            }
          }

          delete newCase.client;

          if (batchClients.length) {
            for (let i = 0; i < batchClients.length; i++) {
              newCase.clientId = batchClients[i].id;
              console.log(newCase);
              await this.$http.post(`${window.CONFIG.legacy_facade_api}/labs`, JSON.stringify(newCase));
            }
            this.$emit("lab-batch-complete");
            console.log("Batch complete");
          } else {
            const jsonData = JSON.stringify(newCase);
            const result = await this.$http.post(`${window.CONFIG.legacy_facade_api}/labs`, jsonData);
            this.$emit("lab-created", result.body);
          }

          this.newCaseDialogFormVisible = false;
          loadingInstance.close();
        }
      });
    },
    updateLabResult: async function(existingCase) {
      this.$refs["observerForm"].validate(async valid => {
        if (valid) {
          delete existingCase._formMode;
          var loadingInstance = Loading.service({ fullscreen: true });
          const jsonData = JSON.stringify(existingCase);
          const result = await this.$http.put(`${window.CONFIG.legacy_facade_api}/labs/${existingCase.id}`, jsonData);
          this.$emit("lab-updated", result.body);
          this.newCaseDialogFormVisible = false;
          loadingInstance.close();
        }
      });
    },
    emitBatchEditData(values) {
      let v = JSON.parse(JSON.stringify(values));
      delete v._formMode;
      delete v._isBatch;
      delete v._presetClient;
      delete v.newCaseFac;
      delete v.clientType;
      this.$emit("lab-batch-edit", v);
    },
    deleteCase: function(i, row) {
      // console.log(row)
      // if (!row) {
      //   row = this.cases[i]
      // }
      this.selectedCaseToDelete = row;
      this.dialogConfirmDeleteVisible = true;
    },
    confirmDeleteCase: function() {
      if (this.selectedCaseToDelete) {
        this.selectedCaseToDelete.active = false;
        this.saveSymtomDetail(this.selectedCaseToDelete);
        this.dialogConfirmDeleteVisible = false;
      }
    },
    newSypmtomsSelected: function() {
      console.log(JSON.stringify(this.newCase.symptoms));
      // console.log(tagsEl)
      // console.log(tagsEl.nodeName)
      // console.log(tagsEl.children)

      var vm = this;
      this.$nextTick(function() {
        console.log(" --- this.$refs ---");
        console.log(vm.$refs);

        var tagsEl = vm.$refs.newcasesymptoms.$refs.tags.firstChild;
        for (var i = 0; i < tagsEl.children.length; i++) {
          var el = tagsEl.children[i];
          console.log(el.textContent);
          for (var j = 0; j < vm.symptoms.length; j++) {
            var s = vm.symptoms[j];
            if (s.custom) {
              console.log("custom skipped");
              continue;
            }
            if (el.textContent === s.displayText) {
              if (el.classList.contains("el-tag--primary")) {
                el.classList.remove("el-tag--primary");
              }
              var tagType = "el-tag--" + vm.tagType(s);
              // console.log(tagType)
              if (!el.classList.contains(tagType)) {
                el.classList.add(tagType);
              }
              break;
            }
          }
          // console.log(vm.symptoms)
        }
      });
    },
    loadSummary: function() {
      // period (monthly | weekly | daily)
      // start & end
      const parameters = this.getFiltersAsQueryString();
      this.$http.get(`/static/mockLabSummary.json?${parameters}`).then(
        response => {
          this.processSummary(response.body);
        },
        error => {
          throw Error(error);
        }
      );
    },
    getIdForItem(item) {
      if (item.clientType !== "Staff") {
        return this.$configStore.patientIdType() ? item[this.$configStore.patientIdType()] : item.patientId;
      } else {
        return item.staffId;
      }
    }
  },
  mounted: function() {
    this.canModify = auth.canModify();
    const end = moment()
      .endOf("month")
      .toDate();
    const start = moment()
      .startOf("month")
      .toDate();
    this.filterDateRange = [start, end];

    var vm = this;
    vm.sites = this.$configStore.data.sites || [];
    vm.units = this.$configStore.data.units || [];
    vm.rooms = this.$configStore.data.rooms || [];
    vm.symptomTypes = this.$configStore.data.symptomTypes || [];
    vm.organismOptions = this.$configStore.data.organismOptions || [];
    vm.customSymptomTypeMap = this.$configStore.data.customSymptomTypeMap || {};
    vm.symptoms = this.$configStore.data.symptoms || [];
    vm.labProceduresOptions = (this.$configStore.data.config.LabProcedures || []).map(p => {
      return {
        label: p,
        value: p
      };
    });
    console.log("loadCustomSypmtoms", this.symptoms);
    vm.labSourceOptions = (this.$configStore.data.config.LabSource || []).map(s => {
      return {
        label: s,
        value: s
      };
    });
    vm.labReasonOptions = (this.$configStore.data.config.LabReason || []).map(r => {
      return {
        label: r,
        value: r
      };
    });
    console.log("loadCustomSypmtoms", this.symptoms);
    this.symptoms.forEach(s => {
      console.log(s.classType, s.displayText);
    });
    vm.organisms = this.$configStore.data.organisms || [];
    vm.precautions = this.$configStore.data.precautions || [];
    vm.labIdPrefix = this.$configStore.labIdPrefix();
    vm.labResults = this.$configStore.labResults();
    vm.origins = this.$configStore.data.origins || [];
    this.dataReady = true;
    this.isRevera = auth.userInfo().org === "revera" || auth.userInfo().org === "revdemo";

    this.clientList = [];
    if (this.sites.length === 1) {
      this.newCaseFac = this.sites[0].id;
      this.newCaseFacSelection();
    }
    this.client = this.$configStore.client || {};

    vm.isLoading = false;

    if (this.labResultData && this.mode === "addOrganism") {
      delete this.labResultData.notes;
    }
    if (this.mode) {
      this.labResultData._formMode = this.mode;
    }
    vm.$nextTick(function() {
      this.showCaseDialog(this.labResultData);
    });
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.el-dialog .el-row {
  line-height: 3em;
}

.filter-group-label {
  padding: 0.5em;
  font-size: 0.8em;
  font-weight: bold;
}

.delete-dialog div {
  line-height: 3em;
}

.delete-dialog .el-dialog div.el-dialog__body {
  padding-top: 0;
}

.graph {
  overflow-x: scroll;
  position: relative;
  width: 100%;
  height: 450px;
  padding: 1em;
}

.graph > canvas {
  position: absolute;
  left: 0;
  top: 0;
  width: 1500px;
  pointer-events: none;
}

.el-card {
  margin-bottom: 1em;
}

div.profile-photo {
  width: 10em;
  height: 10em;
  border-radius: 50%;
  position: absolute;
  right: 2.5em;
  top: 0.5em;
  display: block;
  overflow: hidden;
  border-radius: 50%;
}

.profile-photo img {
  width: auto;
  height: 100%;
}

div.profile-row-photo {
  width: 4em;
  height: 4em;
  overflow: hidden;
  border-radius: 2em;
  border: solid 1px lightgray;
}

.profile-row-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: relative;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.dialog-case-detail div {
  line-height: 3em;
}

.input-boxes {
  border-style: solid;
  border-width: 1px;
  border-color: lightgray;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix {
  clear: both;
}

/*.el-button {
  width: 100%;
}*/

.el-tag {
  margin-right: 0.5em;
}

.color-code-label-0 {
  width: 1em;
  height: 1em;
  background: #00d560;
  display: inline-block;
  vertical-align: middle;
  border-style: solid;
  border-width: 1px;
  border-color: lightgrey;
}
.color-code-label-1 {
  width: 1em;
  height: 1em;
  background: #0080ff;
  display: inline-block;
  vertical-align: middle;
  border-style: solid;
  border-width: 1px;
  border-color: lightgrey;
}
.color-code-label-2 {
  width: 1em;
  height: 1em;
  background: #ffde25;
  display: inline-block;
  vertical-align: middle;
  border-style: solid;
  border-width: 1px;
  border-color: lightgrey;
}
.color-code-label-3 {
  width: 1em;
  height: 1em;
  background: gray;
  display: inline-block;
  vertical-align: middle;
  border-style: solid;
  border-width: 1px;
  border-color: lightgrey;
}
.graph-filter > .el-radio-group {
  padding: 15px;
  float: right;
}
.graph-filter {
  margin-right: 1em;
  margin-left: 3em;
  position: absolute;
  right: 0;
  top: -0.5em;
}
.case-filter > .el-pagination {
  padding-top: 1px;
  float: right;
}
.case-filter > .case-page-size-filte {
  width: 100px;
}
.case-filter > .case-export-button {
  float: left;
  margin-right: 1em;
  margin-top: 1px;
}
.case-filter {
  margin-right: 1em;
  margin-left: 3em;
  position: absolute;
  right: 0;
  top: 0.5em;
}
.filter-title-label {
  margin-left: 2em;
  margin-right: 1em;
  text-align: right;
  line-height: 40px;
  width: 100px;
  font-size: 0.8em;
}
.filter-section > label {
  display: inline-block;
  width: 355px;
}

.filter-section .el-input {
  float: right;
  position: relative;
  width: 195px;
}

.filter-section {
  margin: 1em;
  padding-bottom: 0.7em;
  display: table-cell;
  vertical-align: middle;
  height: 50px;
  padding-top: 0.5em;
}

.filter-section label {
  text-align: right;
}

.filter-section-input {
  float: right;
}
.symptom-status-image {
  width: 36px;
  position: relative;
  margin: auto;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.symptom-detail-status-image {
  width: 36px;
  position: absolute;
  margin: auto;
  margin-left: 5px;
  top: 5px;
}
.case-note {
  max-height: 15em;
  width: 100%;
  overflow-y: scroll;
}

.dialog-case-detail div.case-note-date {
  float: right;
  padding: 1em;
  font-size: 1em;
  font-weight: 500;
  line-height: 1.8em;
}

.dialog-case-detail div.case-note-content {
  padding: 1em;
  border-bottom: 1px solid #eeeeee;
  line-height: 1.8em;
}

.case-note::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 5px;
}

.case-note::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: rgba(0, 0, 0, 0.5);
  -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}

.surveillance .el-tabs__item {
  font-size: 1em;
  padding: 0 10px 0 10px;
}

.surveillance .el-dialog.el-dialog--large {
  width: 80% !important;
}

.el-popover .el-row {
  line-height: 2.5em;
  font-family: sans-serif;
  font-size: 1.2em;
}

.el-popover .el-row .el-switch {
  float: right;
}

.dialog-footer {
  float: right;
  margin: 1em;
  margin-top: 2em;
}

.new-case-dialog .el-form-item__error {
  line-height: normal !important;
}
</style>

<style>
.new-case-dialog .el-form-item__label {
  width: 130px;
}
.new-case-dialog .el-form-item__error {
  line-height: 1;
  margin-left: 130px;
}
.new-case-dialog .labid .el-input {
  line-height: 1;
  width: 200px;
}
.new-case-dialog .el-textarea {
  width: 400px;
}
.new-case-dialog .el-form .profile-photo {
  top: 70px;
}
.new-case-dialog .el-form .new-case-name {
  font-weight: bold;
  margin: 0 0 0 20px;
}
.new-case-dialog.isBatch .selectIndividual .el-select {
  width: 700px;
}
.new-case-dialog.isBatch .selectIndividual .el-checkbox {
  margin-left: 130px;
}
</style>
